// Stored-WorkoutSet -> DraftSet shape construction, shared by
// hydrateFromWorkout (edit/repeat) and draftSetsFromHistoryEntry
// (history copy). Extracted from workoutHydration.ts (Track R: that file
// passed the 500-line budget) along the seam the file header flagged —
// the two functions duplicated ~90 lines of per-kind branch construction.
// Behaviour-preserving: every divergence between the two call sites is
// expressed through StoredSetToDraftOpts, locked by workoutHydration.test.ts.
import {
  fromCanonicalKg,
  type Dropset as DropsetT,
  type DropsetDraft,
  type DropsetStepDraft,
  type WorkoutSet,
} from '@heraion/shared';
import type { DraftSet, WeightUnit } from './state/loggerDraftTypes';

// Move a hydrated parent set's reps/weight into the head of its dropset
// chain so the draft matches the Logger's all-rounds-in-steps model: the
// parent row is a derived summary, every round (top included) lives in
// `dropset.steps` (steps[0] = top). Inverse of buildDropsetSet's promote
// on save, so a saved dropset round-trips through edit unchanged. Sets
// without a chain pass through untouched. The top round's negative-extra
// sign isn't representable on a chain step (no per-step sign toggle), so
// a bodyweight top hydrates as positive extra — matching what the UI can
// capture.
const promoteParentIntoChain = (set: DraftSet): DraftSet => {
  if (set.dropset === null) return set;
  const topStep: DropsetStepDraft = {
    reps: set.reps,
    weight: set.weight,
    completedAt: set.completedAt,
  };
  return {
    ...set,
    reps: '',
    weight: '',
    extraNegative: false,
    dropset: {
      variant: set.dropset.variant,
      steps: [topStep, ...set.dropset.steps],
    },
  };
};

// Pull a stored Dropset back into the typed-input DropsetDraft shape
// used by SetRow. Mirrors the legacy web's `dropsetDraftFromStored`
// (`7ae8e35e:apps/web/src/workout/WorkoutLog.tsx`) exactly: same single-string `weight` field
// for both weighted and bodyweight parents (extra surfaces here for bw
// rows). Pre-1.80 saved sets without the field hydrate as `null` so
// the chain block stays hidden until the user re-attaches one.
const dropsetDraftFromStored = (
  d: DropsetT | undefined,
  isBodyweight: boolean,
  parentCompletedAt: string | null = null,
): DropsetDraft | null => {
  if (!d) return null;
  return {
    variant: d.variant,
    steps: d.steps.map((s) => ({
      reps: String(s.reps),
      weight: isBodyweight
        ? 'extra' in s && typeof s.extra === 'number'
          ? String(s.extra)
          : ''
        : 'weight' in s && typeof s.weight === 'number'
          ? String(s.weight)
          : '',
      // Slice 4: hydrate per-step completion. Newer rows carry the
      // per-step stamp; legacy rows fall back to the parent set's
      // completedAt (treat-as-done when the parent is done — spec
      // §8 history-render rule), and to null when the parent is
      // also pending (rare; mid-draft dropsets).
      completedAt:
        'completedAt' in s && typeof s.completedAt === 'string'
          ? s.completedAt
          : parentCompletedAt,
    })),
  };
};

export interface StoredSetToDraftOpts {
  /** Display unit; canonical kg round-trips losslessly regardless. */
  unit: WeightUnit;
  // Per-row state. hydrateFromWorkout derives these (edit preserves the
  // saved value, repeat blanks history); draftSetsFromHistoryEntry forces
  // the blank slate — "about to perform, not review".
  completedAt: string | null;
  fail: boolean;
  // "This set went to the limit on the open variable" (LogSet.amrap) —
  // the OUTCOME half of AMRAP, never the prescription. Caller-resolved
  // exactly like `fail` because it shares its nature: an execution
  // OUTCOME, not structure. Edit hands back the stored flag so re-saving
  // can't wipe it; repeat and the history copy pass false — cloning the
  // shape of a session must not claim the athlete already took the new
  // set to failure. Only weighted/bodyweight rows read it; cardio/timed
  // have no such field on their WorkoutSet variant yet.
  // There is deliberately no `amrapPrescribed` opt: a stored WorkoutSet
  // records what happened, not what a plan asked for, so every draft
  // built from one starts with the prescription half false (the badge
  // belongs to a live plan-started session, not to a saved row).
  amrap: boolean;
  /** #353: weighted only. Edit hands the stored flag back; absent elsewhere. */
  calibration?: boolean;
  note: string;
  rpe: string;
  rir: string;
  /** PRESCRIPTION half (PE-22 fetta D). Set only by a REPEAT, where the
   *  previous session's effort is carried forward as a TARGET rather than as
   *  something performed again (Antenore, 2026-08-25). A true EDIT leaves it
   *  absent: there `rpe`/`rir` above ARE the measurement. */
  rirPrescribed?: number;
  // Weighted-set fallback when the stored row predates loadedSides
  // (1.45.0). hydrate → 1 (bilateral default); history copy → the entry's
  // current default so a unilateral redo keeps the multiplier.
  weightedLoadedSidesFallback: 1 | 2;
  // Edit/repeat hydration rebuilds the dropset chain + carries prefill
  // provenance; the history-copy path starts blank (dropset null, no
  // weightSource).
  includeDropset: boolean;
  includeWeightSource: boolean;
  // Cardio HR/calories are performance OUTCOMES: hydrate carries them so
  // an edit shows what was recorded; the history copy blanks them (you
  // can't pre-set a heart rate you're about to produce). Distance +
  // duration are structural and always carry.
  includeCardioOutcomes: boolean;
}

// Single source of the per-kind DraftSet construction. Only weighted +
// bodyweight + cardio + timed are produced; canEditOnMobile filters the
// rest before any hydrate path reaches here, and the history copy skips
// them too — the trailing fallback keeps the .min(1) sets constraint
// satisfied if a stray kind ever slips through.
export function storedSetToDraftSet(
  s: WorkoutSet,
  opts: StoredSetToDraftOpts,
): DraftSet {
  const {
    unit,
    completedAt,
    fail,
    amrap,
    calibration,
    note,
    rpe,
    rir,
    rirPrescribed,
    weightedLoadedSidesFallback,
    includeDropset,
    includeWeightSource,
    includeCardioOutcomes,
  } = opts;

  if (s.type === 'weighted') {
    const loadedSides =
      'loadedSides' in s && (s.loadedSides === 1 || s.loadedSides === 2)
        ? s.loadedSides
        : weightedLoadedSidesFallback;
    return promoteParentIntoChain({
      reps: String(s.reps),
      weight: String(fromCanonicalKg(s.weight, unit)),
      unit,
      extraNegative: false,
      completedAt,
      fail,
      prFlash: false,
      note,
      rpe,
      rir,
      ...(rirPrescribed !== undefined ? { rirPrescribed } : {}),
      loadedSides,
      ...(includeWeightSource ? { weightSource: s.weightSource } : {}),
      duration: '',
      distanceM: '',
      avgHrBpm: '',
      calories: '',
      amrapPrescribed: false,
      amrap,
      ...(amrap ? { amrapSaved: true } : {}),
      ...(calibration === true ? { calibration: true } : {}),
      ...(s.side !== undefined ? { side: s.side } : {}),
      dropset: includeDropset
        ? dropsetDraftFromStored(s.dropset, false, completedAt)
        : null,
    });
  }

  if (s.type === 'bodyweight') {
    const extra =
      'extra' in s && typeof s.extra === 'number' ? s.extra : null;
    const extraNegative = extra !== null && extra < 0;
    const extraDisplay =
      extra !== null ? String(fromCanonicalKg(Math.abs(extra), unit)) : '';
    const loadedSides =
      'loadedSides' in s && (s.loadedSides === 1 || s.loadedSides === 2)
        ? s.loadedSides
        : 1;
    return promoteParentIntoChain({
      reps: String(s.reps),
      weight: extraDisplay,
      unit,
      extraNegative,
      completedAt,
      fail,
      prFlash: false,
      note,
      rpe,
      rir,
      ...(rirPrescribed !== undefined ? { rirPrescribed } : {}),
      loadedSides,
      ...(includeWeightSource ? { weightSource: s.weightSource } : {}),
      duration: '',
      distanceM: '',
      avgHrBpm: '',
      calories: '',
      amrapPrescribed: false,
      amrap,
      ...(amrap ? { amrapSaved: true } : {}),
      ...(s.side !== undefined ? { side: s.side } : {}),
      dropset: includeDropset
        ? dropsetDraftFromStored(s.dropset, true, completedAt)
        : null,
    });
  }

  if (s.type === 'cardio') {
    return {
      reps: '',
      weight:
        'weight' in s && typeof s.weight === 'number'
          ? String(fromCanonicalKg(s.weight, unit))
          : '',
      unit,
      extraNegative: false,
      completedAt,
      fail,
      prFlash: false,
      note,
      rpe,
      rir,
      ...(rirPrescribed !== undefined ? { rirPrescribed } : {}),
      loadedSides: 1,
      duration: String(s.duration),
      distanceM:
        'distanceM' in s && typeof s.distanceM === 'number'
          ? String(s.distanceM)
          : '',
      avgHrBpm:
        includeCardioOutcomes &&
        'avgHrBpm' in s &&
        typeof s.avgHrBpm === 'number'
          ? String(s.avgHrBpm)
          : '',
      calories:
        includeCardioOutcomes &&
        'calories' in s &&
        typeof s.calories === 'number'
          ? String(s.calories)
          : '',
      amrapPrescribed: false,
      amrap: false,
      dropset: null,
    };
  }

  if (s.type === 'timed') {
    return {
      reps: '',
      weight:
        'weight' in s && typeof s.weight === 'number'
          ? String(fromCanonicalKg(s.weight, unit))
          : '',
      unit,
      extraNegative: false,
      completedAt,
      fail,
      prFlash: false,
      note,
      rpe,
      rir,
      ...(rirPrescribed !== undefined ? { rirPrescribed } : {}),
      loadedSides: 1,
      duration: String(s.duration),
      distanceM: '',
      avgHrBpm: '',
      calories: '',
      amrapPrescribed: false,
      amrap: false,
      // #41: per set, so a timed set of a reps exercise stays timed.
      timed: true,
      dropset: null,
    };
  }

  return {
    reps: '',
    weight: '',
    unit,
    extraNegative: false,
    completedAt: null,
    fail: false,
    prFlash: false,
    note: '',
    rpe: '',
    rir: '',
    loadedSides: 1,
    duration: '',
    distanceM: '',
    avgHrBpm: '',
    calories: '',
    amrapPrescribed: false,
    amrap: false,
    dropset: null,
  };
}
